import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import PopupDialog from '@/components/third-party/dialog';
import { useAppSelector } from '@/store/hooks';
import { ACTION_BY_ID, FORMS_DEFAULTS, MENU_SEARCH_EVENT, SHORTCUT_ACTIONS, ShortcutAction } from './actions';
import { comboFromEvent, displayCombo, normalizeCombo } from './keys';

/**
 * Oracle Forms style keyboard shortcuts for the whole portal.
 *
 * One capturing keydown listener turns the pressed combination into an action
 * using the company layout (Administration > Keyboard Shortcuts, Forms
 * defaults when none is saved). Screens say what an action means for them with
 * useShortcutActions; the newest registration that handles it wins. Field-level
 * actions (List of Values, Clear Field, Next/Previous Block, Show Keys) work on
 * every screen.
 */

/** return false when the action does not apply right now (the key then works as usual) */
export type ShortcutHandler = (e: KeyboardEvent) => boolean | void;
export type ShortcutHandlers = Partial<Record<ShortcutAction, ShortcutHandler>>;

interface Registration {
  id: number;
  handlers: React.MutableRefObject<ShortcutHandlers>;
}

interface ShortcutContextValue {
  /** action -> key combination in effect for the company */
  keymap: Record<ShortcutAction, string | null>;
  /** true when the company has saved its own layout */
  customized: boolean;
  register: (handlers: React.MutableRefObject<ShortcutHandlers>) => () => void;
  /** stop handling keys (e.g. while a key is being captured on the settings screen) */
  setSuspended: (on: boolean) => void;
  openShowKeys: () => void;
  reload: () => void;
}

const ShortcutContext = createContext<ShortcutContextValue | null>(null);

export const KEYMAP_URL = 'keyboardShortcuts/get';

const FOCUSABLE =
  'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), [role="combobox"]:not([aria-disabled="true"]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const isVisible = (el: Element) => (el as HTMLElement).offsetParent !== null || (el as HTMLElement).getClientRects().length > 0;

/** sends a key to the focused element (MUI opens Autocomplete/Select on ArrowDown) */
const sendKey = (target: Element, key: string) =>
  target.dispatchEvent(new KeyboardEvent('keydown', { key, code: key, bubbles: true, cancelable: true }));

const setNativeValue = (input: HTMLInputElement | HTMLTextAreaElement, value: string) => {
  const proto = input instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(input, value);
  input.dispatchEvent(new Event('input', { bubbles: true }));
};

/** F9: open the list of the focused field */
const listOfValues = (): boolean => {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  if (el.closest('.MuiAutocomplete-root')) {
    if (el.getAttribute('aria-expanded') !== 'true') sendKey(el, 'ArrowDown');
    return true;
  }
  if (el.getAttribute('role') === 'combobox' || el.classList.contains('MuiSelect-select')) {
    sendKey(el, 'ArrowDown');
    return true;
  }
  const pickerButton = el.closest('.MuiFormControl-root, .MuiTextField-root')?.querySelector<HTMLButtonElement>(
    'button[aria-label*="date" i], button[aria-label*="Choose" i]'
  );
  if (pickerButton) {
    pickerButton.click();
    return true;
  }
  return false;
};

/** F5: clear the focused field */
const clearField = (): boolean => {
  const el = document.activeElement as HTMLElement | null;
  if (!el || !(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) return false;
  if (el.readOnly || el.disabled) return true;
  const clearButton = el.closest('.MuiAutocomplete-root')?.querySelector<HTMLButtonElement>('.MuiAutocomplete-clearIndicator');
  if (clearButton) clearButton.click();
  else setNativeValue(el, '');
  return true;
};

/**
 * Shift+PageDown / Shift+PageUp: move between sections marked data-key-block,
 * in data-key-order when given (screens laid out with CSS order), else DOM order.
 */
const moveBlock = (step: 1 | -1): boolean => {
  const blocks = [...document.querySelectorAll('[data-key-block]')]
    .filter(isVisible)
    .map((el, i) => ({ el, order: Number(el.getAttribute('data-key-order') ?? 1000 + i) }))
    .sort((a, b) => a.order - b.order)
    .map((b) => b.el);
  if (!blocks.length) return false;
  const current = (document.activeElement as HTMLElement | null)?.closest('[data-key-block]');
  let index = current ? blocks.indexOf(current) : step === 1 ? -1 : blocks.length;
  for (let n = 0; n < blocks.length; n += 1) {
    index = (index + step + blocks.length) % blocks.length;
    // a block may name where focus lands (data-key-focus), e.g. the first line of a grid
    const preferred = blocks[index].getAttribute('data-key-focus');
    const target = [
      ...(preferred ? blocks[index].querySelectorAll<HTMLElement>(preferred) : []),
      ...blocks[index].querySelectorAll<HTMLElement>(FOCUSABLE),
    ].find(isVisible);
    if (target) {
      target.focus();
      if (target instanceof HTMLInputElement) target.select?.();
      return true;
    }
  }
  return false;
};

export const ShortcutProvider = ({ children }: { children: ReactNode }) => {
  const { loggedUserData } = useAppSelector((state) => state.loginReducer);
  const compCode = loggedUserData?.comp_code ?? null;
  const query = useQuery({
    queryKey: ['keymap', compCode],
    enabled: Boolean(compCode),
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data } = await axios.post<{ status: boolean; result?: { ACTION: string; KEYS: string | null }[] }>(
        KEYMAP_URL,
        {}
      );
      return data?.status ? data.result ?? [] : [];
    },
  });

  const keymap = useMemo(() => {
    const map = { ...FORMS_DEFAULTS };
    (query.data ?? []).forEach((row) => {
      if (row.ACTION in map) map[row.ACTION as ShortcutAction] = normalizeCombo(row.KEYS);
    });
    return map;
  }, [query.data]);
  const byCombo = useMemo(() => {
    const map = new Map<string, ShortcutAction>();
    (Object.keys(keymap) as ShortcutAction[]).forEach((action) => {
      const combo = normalizeCombo(keymap[action]);
      if (combo) map.set(combo, action);
    });
    return map;
  }, [keymap]);

  const registrations = useRef<Registration[]>([]);
  const nextId = useRef(1);
  const suspended = useRef(false);
  const [showKeys, setShowKeys] = useState(false);

  const register = useCallback((handlers: React.MutableRefObject<ShortcutHandlers>) => {
    const entry = { id: nextId.current++, handlers };
    registrations.current = [...registrations.current, entry];
    return () => {
      registrations.current = registrations.current.filter((r) => r.id !== entry.id);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.isTrusted || e.isComposing || suspended.current || e.defaultPrevented) return;
      const combo = comboFromEvent(e);
      const action = combo ? byCombo.get(combo) : undefined;
      if (!action) return;

      // while any dialog is open the screen behind it must not act (F10 in a
      // confirm box would otherwise save the order underneath)
      const inDialog =
        Boolean((document.activeElement as HTMLElement | null)?.closest('[role="dialog"]')) ||
        [...document.querySelectorAll('.MuiDialog-root')].some((d) =>
          isVisible(d.querySelector('.MuiDialog-paper') ?? d)
        );
      let handled = false;
      if (!inDialog) {
        for (const reg of [...registrations.current].reverse()) {
          const handler = reg.handlers.current[action];
          if (handler && handler(e) !== false) {
            handled = true;
            break;
          }
        }
      }
      if (!handled) {
        if (action === 'LIST_OF_VALUES') handled = listOfValues();
        else if (action === 'CLEAR_FIELD')
          // on an entry screen F5 must not reload the page and lose the entry
          handled = clearField() || registrations.current.length > 0;
        else if (action === 'NEXT_BLOCK' && !inDialog) handled = moveBlock(1);
        else if (action === 'PREVIOUS_BLOCK' && !inDialog) handled = moveBlock(-1);
        else if (action === 'SHOW_KEYS') {
          setShowKeys(true);
          handled = true;
        } else if (action === 'MENU_SEARCH' && !inDialog) {
          window.dispatchEvent(new Event(MENU_SEARCH_EVENT));
          handled = true;
        }
      }
      if (handled) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [byCombo]);

  const value = useMemo<ShortcutContextValue>(
    () => ({
      keymap,
      customized: (query.data ?? []).length > 0,
      register,
      setSuspended: (on) => {
        suspended.current = on;
      },
      openShowKeys: () => setShowKeys(true),
      reload: () => {
        query.refetch();
      },
    }),
    [keymap, query, register]
  );

  return (
    <ShortcutContext.Provider value={value}>
      {children}
      {showKeys && (
        <ShowKeysDialog
          keymap={keymap}
          available={(id) =>
            Boolean(ACTION_BY_ID[id].generic) || registrations.current.some((r) => Boolean(r.handlers.current[id]))
          }
          onClose={() => setShowKeys(false)}
        />
      )}
    </ShortcutContext.Provider>
  );
};

const ShowKeysDialog = ({
  keymap,
  available,
  onClose,
}: {
  keymap: Record<ShortcutAction, string | null>;
  available: (id: ShortcutAction) => boolean;
  onClose: () => void;
}) => {
  const rows = SHORTCUT_ACTIONS.filter((a) => keymap[a.id] && available(a.id));
  return (
    <PopupDialog isOpen setOpen={(open) => !open && onClose()} header="Keyboard shortcuts on this screen" onReleaseClick={onClose} maxWidth="sm">
      <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 1.5 }}>
        Tab / Shift+Tab move between fields and Enter confirms. The company layout is set in
        Administration › Keyboard Shortcuts.
      </Typography>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <tbody>
          {rows.map((a) => (
            <Box component="tr" key={a.id} sx={{ '& td': { py: 0.75, borderBottom: '1px solid #EEF1F5' } }}>
              <td style={{ width: 150 }}>
                <Chip size="small" label={displayCombo(keymap[a.id])} sx={{ fontFamily: 'monospace', fontWeight: 700 }} />
              </td>
              <td>
                <Stack>
                  <b>{a.label}</b>
                  <span style={{ color: '#667085', fontSize: '0.75rem' }}>{a.description}</span>
                </Stack>
              </td>
            </Box>
          ))}
        </tbody>
      </Box>
    </PopupDialog>
  );
};

export const useShortcutContext = () => {
  const ctx = useContext(ShortcutContext);
  if (!ctx) throw new Error('useShortcutContext must be used inside ShortcutProvider');
  return ctx;
};

/**
 * Says what keyboard actions mean on this screen (or part of it). Handlers
 * always see the latest props; return false when the action does not apply
 * at the moment (e.g. focus is outside this part).
 */
export const useShortcutActions = (handlers: ShortcutHandlers, enabled = true) => {
  const ctx = useContext(ShortcutContext);
  const ref = useRef(handlers);
  ref.current = handlers;
  useEffect(() => {
    if (!ctx || !enabled) return undefined;
    return ctx.register(ref);
  }, [ctx?.register, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
};
